import { Component, Inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize } from 'rxjs';
import { IBadgeModel } from '../../../../models/general.model';
import { IPostModel } from '../../../../models/post.model';
import { IUserProfileModel } from '../../../../models/user.model';
import { GeneralService } from '../../../../services/api/general/general.service';
import { PostService } from '../../../../services/api/post/post.service';
import { UserService } from '../../../../services/api/user/user.service';
import { CreatePostEventService } from '../../../../services/helper/create-post-event/create-post-event.service';
import { LoaderService } from '../../../../services/loader/loader.service';
import { BasePopupComponent } from '../base-popup/base-popup.component';
import { SuccessPopupComponent } from '../success-popup/success-popup.component';

@Component({
    selector: 'app-badge-popup',
    standalone: false,
    templateUrl: './badge-popup.component.html',
    styleUrl: './badge-popup.component.scss',
})
export class BadgePopupComponent extends BasePopupComponent implements OnInit {
    protected badges = signal<IBadgeModel[]>([]);
    protected getUserProfileDetails = signal<IUserProfileModel | null>(null);
    protected selectedBadge?: IBadgeModel;

    constructor(
        @Inject(MAT_DIALOG_DATA) protected override data: IPostModel,
        protected override dialogRef: MatDialogRef<BadgePopupComponent>,
        private createPostEventService: CreatePostEventService,
        private dialog: MatDialog,
        private generalService: GeneralService,
        private loaderService: LoaderService,
        private matSnackBar: MatSnackBar,
        private postService: PostService,
        private userService: UserService,
    ) {
        super(data, dialogRef);
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.loadBadges();
        this.getUserProfile();
    }

    protected selectBadge(badge: IBadgeModel): void {
        this.selectedBadge = badge;
    }

    protected sendBadge(): void {
        if (!this.selectedBadge) {
            return;
        }

        const userCoins = this.getUserProfileDetails()?.coin || 0;

        if (this.selectedBadge.coin > userCoins) {
            this.showInsufficientBalanceAndReopen();
            return;
        }

        this.loaderService.show();
        this.postService
            .giveBadgeToPost(this.data.id, this.selectedBadge.id)
            .pipe(
                finalize(() => {
                    this.loaderService.hide();
                }),
            )
            .subscribe({
                next: () => {
                    this.dialogRef.close(true);
                    this.matSnackBar.open('Badge sent!', '', { duration: 2000 });
                    this.createPostEventService.notifyBadgeSent(this.data.id);
                },
                error: (error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Failed to send badge:', error);
                },
            });
    }

    private getUserProfile(): void {
        this.loaderService.show();
        this.userService.getUserProfile().subscribe({
            next: (res) => {
                this.getUserProfileDetails.set(res);
            },
            error: (error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Error getting user profile', error);
            },
            complete: () => {
                this.loaderService.hide();
            },
        });
    }

    private loadBadges(): void {
        this.loaderService.show();
        this.generalService
            .getBadgesListing()
            .pipe(finalize(() => this.loaderService.hide()))
            .subscribe({
                next: (badges) => {
                    this.badges.set(badges);
                },
                error: (error) => {
                    console.error('Error on get badges', error);
                },
            });
    }

    private showInsufficientBalanceAndReopen(): void {
        const popupConfig = SuccessPopupComponent.getDialogConfig();
        popupConfig.data = {
            title: 'Need more Seeds?',
            message: 'You need a few more Seeds to send this. Please top up your balance to continue sowing.',
            iconPath: '/assets/images/shared/seed-icon.svg',
            confirmButtonLabel: 'Top Up',
        };

        this.dialogRef.close();
        this.dialog
            .open(SuccessPopupComponent, popupConfig)
            .afterClosed()
            .subscribe(() => {
                const popupConfig = BadgePopupComponent.getDialogConfig();
                popupConfig.data = this.data;
                this.dialog.open(BadgePopupComponent, popupConfig);
            });
    }
}
